import React, { useState } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import styled from 'styled-components';
import UserIcon from '~/assets/icons/user.svg';
import ChevronIcon from '~/assets/icons/chevron.svg';
import EmailIcon from '~/assets/icons/email.svg';
import PhoneIcon from '~/assets/icons/phone.svg';

const selector = (s: TrainingState) => select(s, 'companyContacts');

const Chevron = styled(ChevronIcon)`
  margin-left: 8px;
  width: 12px;
  height: 18px;
  /* POC cards are always light chrome. --color-black inverts in dark mode
     (#EFF0F1) → near-invisible on white (CAT-1586). Match name-text. */
  color: #2e2e2e;
  stroke: currentColor;

  path {
    stroke: currentColor;
  }
`;

const PointOfContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  /* Shrink-to-fit modal: contribute 0 intrinsic width so long Other Details
     wrap inside the help panel instead of stretching it to the viewport. */
  width: 0;
  min-width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  h3 {
    color: dark;
    font-family: ralewayBold;
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }
`;

const CollapsSectionContainer = styled.div`
  border-radius: var(--8, 8px);
  border: 1px solid var(--Grey-3, #e0e0e0);
  background: var(--Grey-1, #fff);
  padding: 4px 20px;
  cursor: pointer;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  /* Keep visible so EDS double focus ring is not clipped. */
  overflow: visible;

  .collaps-section-header {
    position: relative;
    display: flex;
    flex-direction: row;
    cursor: pointer;
    border-radius: 4px;

    &:focus {
      outline: none;
    }

    /* EDS double ring: outer focus-ring + inner separator (same as Button). */
    &:focus-visible {
      outline: 2px solid var(--color-focus-ring);
      outline-offset: 2px;
    }

    &:focus-visible::after {
      content: '';
      display: block;
      position: absolute;
      border-radius: inherit;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border: 2px solid var(--color-focus-ring-inner);
      pointer-events: none;
    }

    .chevron {
      margin-left: auto;
      margin-top: 20px;

      svg {
        transform: rotate(90deg);
      }

      .open {
        transform: rotate(270deg);
      }
    }

    .name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      .name-text {
        padding-top: 4px;
        color: var(--Grey-6, #2e2e2e);
        font-size: 18px;
        font-weight: 700;
        line-height: 150%;
        font-family: ralewayBold;
      }
      .department {
        color: var(--Grey-5, #6f6f6f);
        font-family: Raleway;
        font-size: 12px;
        font-weight: 400;
        line-height: 150%;
      }
    }
  }

  .collaps-section-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;

    .other-info {
      display: flex;
      flex-direction: column;
      min-width: 0;
      max-width: 100%;

      b {
        color: var(--Grey-6, #2e2e2e) !important;
        font-family: ralewayBold;
        font-size: 12px;
        font-weight: 700;
        line-height: 150%;
        text-transform: uppercase;
      }

      /* Description body — wrap inside the card, never expand the modal. */
      > div {
        max-width: 100%;
        color: var(--Grey-5, #6f6f6f);
        font-size: 14px;
        font-weight: 400;
        line-height: 150%;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
    }

    .email {
      padding-top: 10px;
      font-family: Raleway;
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;

      a {
        display: flex;
        align-items: center;
        gap: 8px;
        /* Card chrome is always light; beat DarkStyle a { accent-fg !important }. */
        color: var(--color-accent-on-light) !important;
        text-decoration: underline;
        min-width: 0;
        overflow-wrap: anywhere;
        word-break: break-word;

        svg {
          flex-shrink: 0;
        }

        svg path {
          fill: currentColor;
        }
      }
    }
    .phone {
      font-family: Raleway;
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;

      a {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--color-accent-on-light) !important;
        text-decoration: underline;
        min-width: 0;
        overflow-wrap: anywhere;
        word-break: break-word;

        svg {
          flex-shrink: 0;
        }

        svg path {
          fill: currentColor;
        }
      }
    }
    .other-info {
      color: var(--Grey-5, #6f6f6f);
    }
  }

  .icon {
    margin-top: 5px;
    margin-right: 12px;
    display: flex;
    width: 48px;
    height: 48px;
    padding: 5.85px 6.825px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    background: var(--Grey-2, #f5f5f5);
    border-radius: 50%;
  }
`;

const CollapsSection = ({
  data,
  index,
  openIndex,
  setOpenIndex
}: {
  data: {
    name?: string;
    department?: string;
    email?: string;
    phone?: string;
    otherInfo?: string;
  };
  index: number;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
}) => {
  const onClickEvent = () => {
    if (openIndex == index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const onKeyDownEvent = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClickEvent();
    }
  };

  return (
    <CollapsSectionContainer>
      <div
        className="collaps-section-header"
        onClick={onClickEvent}
        onKeyDown={onKeyDownEvent}
        role="button"
        tabIndex={0}
      >
        <div className="icon">
          <UserIcon />
        </div>
        <div className="name">
          <div className="name-text">{data.name}</div>
          <div className="department">{data.department}</div>
        </div>
        <div className="chevron">
          <Chevron className={openIndex == index ? 'open' : ''} />
        </div>
      </div>
      <div className="collaps-section-content">
        {openIndex == index && data.email && (
          <div className="email">
            <a href={`mailto:${data.email}`}>
              <EmailIcon />
              {data.email}
            </a>
          </div>
        )}
        {openIndex == index && data.phone && (
          <div className="phone">
            <a href={`tel:${data.phone}`}>
              <PhoneIcon />
              {data.phone}
            </a>
          </div>
        )}
        {openIndex == index && data.otherInfo && (
          <div className="other-info">
            <b>{t`Other Details`}</b>
            <div>{data.otherInfo}</div>
          </div>
        )}
      </div>
    </CollapsSectionContainer>
  );
};

const PointOfContact = ({
  title = t`Your point of contact`
}: {
  title?: string;
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { companyContacts } = useTrainingStore(selector);

  if (!companyContacts || companyContacts.length === 0) return null;

  return (
    <PointOfContactContainer>
      <h3>{title}</h3>
      {companyContacts.map((contact, index) => (
        <CollapsSection
          key={index}
          data={contact}
          openIndex={openIndex}
          setOpenIndex={setOpenIndex}
          index={index}
        />
      ))}
    </PointOfContactContainer>
  );
};

export default PointOfContact;
